const cartDrawerScriptLoads = new Map();

function cartDrawerPublicationError(stage, cause) {
  if (cause?.code === 'SV_CART_UI_PUBLICATION_FAILED') return cause;
  const error = new Error(
    theme.strings?.cartError
    || 'Your cart changed, but the cart drawer could not be refreshed. Please reload and check your cart.'
  );
  error.code = 'SV_CART_UI_PUBLICATION_FAILED';
  error.stage = stage;
  error.cause = cause;
  return error;
}

function cartDrawerDeadline(options = {}) {
  return Number.isFinite(options.deadline) ? options.deadline : Date.now() + 6000;
}

function waitForCartDrawerScript(promise, deadline) {
  const remaining = deadline - Date.now();
  if (remaining <= 0) {
    return Promise.reject(cartDrawerPublicationError('script-load', new Error('Cart refresh timed out.')));
  }
  let timer = null;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => {
        reject(cartDrawerPublicationError('script-load', new Error('Cart dependency timed out.')));
      }, remaining);
    })
  ]).finally(() => clearTimeout(timer));
}

async function cartDrawerFetch(url, deadline, stage) {
  const remaining = deadline - Date.now();
  if (remaining <= 0) throw cartDrawerPublicationError(stage, new Error('Cart refresh timed out.'));
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), remaining);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    throw cartDrawerPublicationError(stage, error);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Loads one cart dependency and proves its custom element registered.
 * Concurrent renders share the same in-flight script instead of racing it.
 */
function loadScript(src, expectedElement, deadline) {
  if (customElements.get(expectedElement)) return Promise.resolve();
  const absoluteSrc = new URL(src, window.location.href).href;
  if (cartDrawerScriptLoads.has(absoluteSrc)) {
    return waitForCartDrawerScript(cartDrawerScriptLoads.get(absoluteSrc), deadline);
  }

  const promise = new Promise((resolve, reject) => {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      reject(cartDrawerPublicationError('script-load', new Error('Cart refresh timed out.')));
      return;
    }

    const scripts = typeof document.querySelectorAll === 'function'
      ? [...document.querySelectorAll('script[src]')]
      : [];
    let script = scripts.find((candidate) => candidate.src === absoluteSrc);
    const created = !script;
    if (!script) {
      script = document.createElement('script');
      script.src = src;
    }

    let timer = null;
    const cleanup = () => {
      clearTimeout(timer);
      script.removeEventListener?.('load', onLoad);
      script.removeEventListener?.('error', onError);
    };
    const fail = (cause) => {
      cleanup();
      script.remove?.();
      reject(cartDrawerPublicationError('script-load', cause));
    };
    const onLoad = () => {
      if (!customElements.get(expectedElement)) {
        fail(new Error(`Cart dependency did not register ${expectedElement}.`));
        return;
      }
      cleanup();
      resolve();
    };
    const onError = () => fail(new Error(`Could not load ${absoluteSrc}.`));

    script.addEventListener?.('load', onLoad, { once: true });
    script.addEventListener?.('error', onError, { once: true });
    timer = setTimeout(() => fail(new Error('Cart dependency timed out.')), remaining);
    if (created) document.body.appendChild(script);
  }).finally(() => {
    cartDrawerScriptLoads.delete(absoluteSrc);
  });

  cartDrawerScriptLoads.set(absoluteSrc, promise);
  return waitForCartDrawerScript(promise, deadline);
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.revealRequest = 0;
      this.renderOpenTimer = null;
      this.quickAddOverlayTransition = null;
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      if (this.renderOpenTimer !== null) clearTimeout(this.renderOpenTimer);
      this.renderOpenTimer = null;
      this.revealRequest += 1;
      this.clearQuickAddOverlayDelay();
      this.classList.toggle('cart-drawer--opening', false);
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
      this.removeEventListener('touchstart', this.footerTouchStartHandler);
      this.removeEventListener('touchmove', this.footerTouchMoveHandler);
      this.removeEventListener('touchend', this.footerTouchEndHandler);
      this.removeEventListener('touchcancel', this.footerTouchEndHandler);
      this.removeEventListener('on:cart-drawer:before-close', this.footerTouchEndHandler);
      this.resetFooterTouch();

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      this.footerTouchStartHandler = this.handleFooterTouchStart.bind(this);
      this.footerTouchMoveHandler = this.handleFooterTouchMove.bind(this);
      this.footerTouchEndHandler = this.handleFooterTouchEnd.bind(this);
      this.addEventListener('touchstart', this.footerTouchStartHandler, { passive: true });
      this.addEventListener('touchmove', this.footerTouchMoveHandler, { passive: false });
      this.addEventListener('touchend', this.footerTouchEndHandler);
      this.addEventListener('touchcancel', this.footerTouchEndHandler);
      this.addEventListener('on:cart-drawer:before-close', this.footerTouchEndHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = () => {
        const refresh = window.SVCart
          ? window.SVCart.enqueue(
              'reconcile:cart-drawer-event-refresh',
              () => null,
              {
                readCart: false,
                publish: ({ uiRefreshed, deadline }) => (
                  uiRefreshed ? undefined : this.refresh(false, { deadline })
                )
              }
            )
          : this.refresh();
        refresh.catch((error) => {
          // EventTarget does not await returned promises. refresh() has already
          // surfaced the visible error, so consume the rejection explicitly.
          console.warn('Cart drawer event refresh failed.', error);
        });
      };
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Starts forwarding a vertical gesture from the fixed checkout area to the cart scrollport.
     * @param {TouchEvent} evt - Touch event.
     */
    handleFooterTouchStart(evt) {
      this.resetFooterTouch();
      if (
        this.dataset.stickySummary !== 'true'
        || evt.touches.length !== 1
        || !evt.target.closest('.cart-drawer__checkout-buttons')
      ) return;

      const scrollTarget = this.querySelector('.drawer__content');
      if (!scrollTarget) return;

      const touch = evt.touches[0];
      this.footerTouch = {
        identifier: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        startScrollTop: scrollTarget.scrollTop,
        isVertical: false,
        scrollTarget
      };
    }

    /**
     * Scrolls the cart content instead of allowing iOS to overscroll the fixed page body.
     * @param {TouchEvent} evt - Touch event.
     */
    handleFooterTouchMove(evt) {
      if (!this.footerTouch || evt.touches.length !== 1) return;

      const touch = Array.from(evt.touches).find(
        (activeTouch) => activeTouch.identifier === this.footerTouch.identifier
      );
      if (!touch) {
        this.resetFooterTouch();
        return;
      }

      const deltaX = touch.clientX - this.footerTouch.startX;
      const deltaY = touch.clientY - this.footerTouch.startY;

      if (!this.footerTouch.isVertical) {
        if (Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) return;
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          this.resetFooterTouch();
          return;
        }

        this.footerTouch.isVertical = true;
      }

      this.footerTouch.scrollTarget.scrollTop = this.footerTouch.startScrollTop - deltaY;
      if (evt.cancelable) evt.preventDefault();
    }

    /**
     * Stops forwarding a fixed-footer gesture.
     */
    handleFooterTouchEnd() {
      this.resetFooterTouch();
    }

    /**
     * Clears the active fixed-footer gesture state.
     */
    resetFooterTouch() {
      this.footerTouch = null;
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      if (this.renderOpenTimer !== null) clearTimeout(this.renderOpenTimer);
      this.renderOpenTimer = null;
      this.clearQuickAddOverlayDelay();
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      const revealRequest = ++this.revealRequest;
      if (quickAddDrawer && overlay) {
        this.quickAddOverlayTransition = { overlay, revealRequest };
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        if (this.revealRequest !== revealRequest) {
          this.clearQuickAddOverlayDelay(revealRequest);
          return;
        }
        const deferReveal = window.matchMedia('(max-width: 768.98px)').matches;
        this.classList.toggle('cart-drawer--opening', true);

        // Hide WebKit's transient body scroll-lock offset until its fixed layer is settled.
        if (deferReveal) {
          this.style.opacity = '0';
        } else {
          this.style.removeProperty('opacity');
        }

        super.open(opener, elementToFocus, () => {
          if (this.revealRequest !== revealRequest) return;
          this.classList.toggle('cart-drawer--opening', false);
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (deferReveal) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (this.revealRequest === revealRequest) this.style.removeProperty('opacity');
            });
          });
        }

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        this.clearQuickAddOverlayDelay(revealRequest);
      }, quickAddDrawer ? 200 : 0);
    }

    clearQuickAddOverlayDelay(revealRequest = null) {
      const transition = this.quickAddOverlayTransition;
      if (!transition || (revealRequest !== null && transition.revealRequest !== revealRequest)) return;
      transition.overlay.style.transitionDelay = '';
      this.quickAddOverlayTransition = null;
    }

    close(callback) {
      if (this.renderOpenTimer !== null) clearTimeout(this.renderOpenTimer);
      this.renderOpenTimer = null;
      this.revealRequest += 1;
      this.clearQuickAddOverlayDelay();
      this.classList.toggle('cart-drawer--opening', false);
      super.close(callback);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems = false, options = {}) {
      const deadline = cartDrawerDeadline(options);
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          await cartItems.refresh({ deadline, announceError: options.announceError });
          return { ok: true, surface: 'drawer', mode: 'child' };
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const sections = await cartDrawerFetch(
            `?sections=${response.join(',')}`,
            deadline,
            'drawer-section-fetch'
          );
          await this.renderContents({ sections }, false, { deadline });
          return { ok: true, surface: 'drawer', mode: 'full' };
        }
      } catch (error) {
        const publicationError = cartDrawerPublicationError('drawer-refresh', error);
        console.log(publicationError); // eslint-disable-line
        if (
          options.announceError !== false
          && publicationError.svCartErrorDispatched !== true
        ) {
          publicationError.svCartErrorDispatched = true;
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: publicationError.message }
          }));
        }
        throw publicationError;
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true, options = {}) {
      const deadline = cartDrawerDeadline(options);
      if (!this.scriptsLoaded) {
        await loadScript(theme.scripts.cartItems, 'cart-items', deadline);

        if (this.dataset.shippingCalculator === 'true') {
          await loadScript(
            theme.scripts.countryProvinceSelector,
            'country-province-selector',
            deadline
          );
          await loadScript(theme.scripts.shippingCalculator, 'shipping-calculator', deadline);
        }

        this.scriptsLoaded = true;
      }

      const replacements = this.getSectionsToRender().map((section) => {
        const sectionHtml = data?.sections?.[section.section];
        if (typeof sectionHtml !== 'string') {
          throw cartDrawerPublicationError(
            'drawer-section-validate',
            new Error(`Missing cart section ${section.section}.`)
          );
        }
        const el = document.getElementById(section.id);
        if (!el) return null;
        return {
          el,
          html: CartDrawer.getElementHTML(sectionHtml, section.selector),
          previousNodes: Array.from(el.childNodes || []),
          previousHtml: el.innerHTML
        };
      }).filter(Boolean);

      if (Date.now() > deadline) {
        throw cartDrawerPublicationError('drawer-dom-publication', new Error('Cart refresh timed out.'));
      }

      const applied = [];
      try {
        replacements.forEach((replacement) => {
          replacement.el.innerHTML = replacement.html;
          applied.push(replacement);
        });
      } catch (error) {
        applied.reverse().forEach((replacement) => {
          if (typeof replacement.el.replaceChildren === 'function') {
            replacement.el.replaceChildren(...replacement.previousNodes);
          } else {
            replacement.el.innerHTML = replacement.previousHtml;
          }
        });
        throw cartDrawerPublicationError('drawer-dom-publication', error);
      }

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      CartDrawer.recalculateCssVarHeights();

      if (this.renderOpenTimer !== null) clearTimeout(this.renderOpenTimer);
      this.renderOpenTimer = null;
      if (openDrawer && this.getAttribute('open') === null) {
        const renderRequest = this.revealRequest;
        this.renderOpenTimer = setTimeout(() => {
          this.renderOpenTimer = null;
          if (this.revealRequest !== renderRequest || this.isConnected === false) return;
          this.open();
        });
      }

      return { ok: true, surface: 'drawer', mode: 'sections' };
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
